import React, { useMemo, useState } from "react"

import { COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import {
    OneVerifyIntegrationCategory,
    OneVerifyIntegrationSupportRequestType,
} from "@common-shared/types/integrations.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { integrationsSubmitSupportRequest } from "../../../store/thunks/integrations.thunks"

export const EraUploadCsvAccessRequestComponent: React.FunctionComponent<
    IEraUploadCsvAccessRequestComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [submitError, setSubmitError] = useState<string | null>(null)
    const [didSubmitRequest, setDidSubmitRequest] = useState<boolean>(false)

    const isSubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isIntegrationSupportRequestLoading()
    }, [runtimeState.isLoading])

    const onRequestAccess = () => {
        setSubmitError(null)

        if (!selectedPracticeRole) {
            setSubmitError(
                "Practice context is not available. Refresh the page and try again.",
            )

            return
        }

        dispatch(
            integrationsSubmitSupportRequest({
                practice: selectedPracticeRole.practice,
                payload: {
                    requestType:
                        OneVerifyIntegrationSupportRequestType.requestEraCsvAccess,
                    integrationCategory: OneVerifyIntegrationCategory.eraData,
                    integrationId:
                        COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload
                            .integrationId,
                    integrationName: "Upload ERA",
                },
                onSuccess: () => {
                    setDidSubmitRequest(true)
                },
            }),
        )
    }

    return (
        <div
            className={`
                rounded-[8px] px-[20px] py-[24px] bg-brand-offWhite
                border border-solid border-brand-warmLight2
                ${props.className ?? ""}
            `}
        >
            <PrimaryText typography="subtitleSemibold">
                CSV upload is not enabled for your practice yet
            </PrimaryText>

            <PrimaryText
                className="mt-[8px] max-w-[640px]"
                weight="medium"
                color={NIRVANA_COLORS.brand.grey7}
            >
                Request access and our customer success team will follow up to
                enable CSV uploads for your account.
            </PrimaryText>

            {didSubmitRequest ? (
                <PrimaryText
                    className="mt-[16px]"
                    weight="medium"
                    color={NIRVANA_COLORS.brand.greenDark}
                >
                    Thanks — we received your request and will follow up.
                </PrimaryText>
            ) : (
                <ButtonElement
                    className="mt-[16px]"
                    type="primary"
                    htmlType="button"
                    label="Request CSV access"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={onRequestAccess}
                />
            )}

            {submitError ? (
                <PrimaryText
                    className="mt-[12px]"
                    size={13}
                    color={NIRVANA_COLORS.brand.error}
                >
                    {submitError}
                </PrimaryText>
            ) : null}
        </div>
    )
}

interface IEraUploadCsvAccessRequestComponentProps {
    className?: string
}
